// ─── Sign-in error text (tooti#256) ───────────────────────────────────────────
//
// Pure function, kept out of KeyEntry.jsx so that file only exports
// components (react-refresh) and so the mapping is unit-testable on its own.
//
// The panel used to show «خطایی رخ داد» both when the backend refused and when
// no answer came back at all. Those need opposite next steps (read the
// server's reason / check the network or VPN), and telling them apart once
// took a full round of server log digging (2026-10-12: the request never
// reached nginx, the browser's WARP egress had dropped it).

import { classifyFailure } from '../../lib/practiceSession'
import { errorCode, errorMessage } from '../../services/api'

export const NETWORK_ERROR =
  'ارتباط با سرور برقرار نشد. اتصال اینترنت یا VPN را بررسی کنید و دوباره امتحان کنید.'

/** The one thing a refused username + password says: which part was wrong is not for the screen to tell. */
export const WRONG_CREDENTIALS = 'نام کاربری یا رمز نادرست است.'
export const TOO_MANY_ATTEMPTS = 'تلاش‌های زیادی شد. کمی بعد دوباره امتحان کنید.'

/**
 * What the username + password form shows for a failed `auth_adminLogin`:
 * the uniform 401 → one sentence for every kind of refusal (unknown name,
 * wrong password, not an admin, locked — the server says the same for all,
 * and so does the screen); the per-address 429 → try later; anything else
 * (no answer, a proxy page) → what the other sign-in paths say.
 */
export function passwordLoginError(error) {
  const status = error?.response?.status
  if (status === 401) return WRONG_CREDENTIALS
  if (status === 429) return TOO_MANY_ATTEMPTS
  return signInErrorMessage(error)
}

export const PASSWORD_LENGTH_ERROR = 'رمز جدید باید ۱۰ تا ۱۲۸ نویسه باشد.'

/** What «تغییر رمز» shows for a failed `auth_adminChangePassword`. */
export function changePasswordError(error) {
  const code = errorCode(error)
  if (code === 'current_password_invalid') return 'رمز فعلی نادرست است.'
  if (code === 'password_unchanged') return 'رمز جدید باید با رمز فعلی فرق کند.'
  if (code === 'password_unacceptable' || code === 'invalid_request') return PASSWORD_LENGTH_ERROR
  if (code === 'password_not_set') return 'برای این حساب هنوز رمزی تعریف نشده است.'
  return signInErrorMessage(error)
}

/**
 * What the sign-in form shows for a failed `otp-request`, `otp-verify` or
 * key check:
 *
 * - an error our own code threw (otpVerify's `not_admin`) → its message;
 * - no answer at all (dropped, offline, timed out) → NETWORK_ERROR;
 * - an answer with `message` (429, wrong code, …) → the server's text;
 * - an answer without one (an HTML 502 from a proxy) → the status code, so a
 *   screenshot alone says where it broke.
 */
export function signInErrorMessage(error) {
  if (!error?.isAxiosError) {
    return typeof error?.message === 'string' && error.message ? error.message : errorMessage(error)
  }
  if (classifyFailure(error) === 'offline') return NETWORK_ERROR
  return errorMessage(error, `خطای سرور (${error.response.status}). دوباره تلاش کنید.`)
}
